<template>
  <div>
    <v-card
      :class="$style.leads_widget"
      :loading="leads_loading"
    >
      <div
        :class="$style.leads_widget_title"
        class="mb-6"
      >
        <span class="display-3">Количество лидов</span>
        <span v-if="!is_filters">за текущий месяц</span>
      </div>

      <template v-if="is_filters">
        <div class="d-flex mb-6">
          <v-row>
            <v-col
              cols="12"
              xl="4"
              lg="6"
              md="12"
              sm="6"
              xs="12"
            >
              <DatepickerRange
                :date_prop="filters.lead_dates"
                label="Период"
                type_date='period'
                :class="[$style.datepicker, $style.filterField, $style.datepickerMargin]"
                dense
                clearable
                range
                small_info
                @date_changed="handle_change_dates"
              />
            </v-col>
            <v-col
              cols="12"
              xl="4"
              lg="6"
              md="12"
              sm="6"
              xs="12"
            >
              <Select
                :value="filters.region"
                :items="lead_regions"
                :class="[$style.select, $style.filterField]"
                label="Регион"
                dense
                hide-details
                clearable
                blue_icon
                @input="filter_update('region', $event, 'leads')"
              />
            </v-col>
          </v-row>
        </div>
      </template>

      <template v-if="is_filters">
        <div :class="$style.leads_widget_chart">
          <div
            v-if="analytics_total_count > 0 && analytics_leads.length > 0"
            id="chart"
            :class="$style.chart_wrapper"
          >
            <apexchart
              type="bar"
              :options="chartOptions"
              :series="series"
            />
          </div>

        </div>
        <div :class="$style.leads_widget_discription">
          <div :class="$style.discription_all_leads">
            <div>
              <span>Общее количество лидов</span>
            </div>
            <div>
              <span class="font-weight-bold">{{ analytics_total_count }}</span>
            </div>
          </div>

          <div
            v-for="item in analytics_leads"
            :key="item.title"
            :class="$style.leads_discription_items"
          >
            <div :class="$style.leads_discription_item">
              <div
                :class="[$style.leads_discription_label_color]"
                :style="{ backgroundColor: colors[analytics_leads.indexOf(item)] }"
              />
              <span class="ml-4 mr-1">{{ item.title }} </span>
              <span v-if="item.total_price"> - {{ item.total_price }} ₽</span>
            </div>
            <div>
              <span class="font-weight-bold">{{ item.value }}</span>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div :class="$style.leads_widget_chart">
          <div
            v-if="total_count > 0 && lead_items.length > 0"
            id="chart"
            :class="$style.chart_wrapper"
          >
            <apexchart
              type="bar"
              :options="chartOptions"
              :series="series"
            />
          </div>

        </div>
        <div :class="$style.leads_widget_discription">
          <div :class="$style.discription_all_leads">
            <div>
              <span>Общее количество лидов</span>
            </div>
            <div>
              <span class="font-weight-bold">{{ total_count }}</span>
            </div>
          </div>

          <div
            v-for="item in lead_items"
            :key="item.title"
            :class="$style.leads_discription_items"
          >
            <div :class="$style.leads_discription_item">
              <div
                :class="[$style.leads_discription_label_color]"
                :style="{ backgroundColor: colors[lead_items.indexOf(item)] }"
              />
              <span class="ml-4 mr-1">{{ item.title }} </span>
              <span v-if="item.total_price"> - {{ item.total_price }} ₽</span>
            </div>
            <div>
              <span class="font-weight-bold">{{ item.value }}</span>
            </div>
          </div>
        </div>
      </template>
    </v-card>
  </div>
</template>

<script lang="ts">
import {
  Vue, Component, Prop, Emit,
} from 'vue-property-decorator';
import { widgets_store, analytics_store } from '@/store';
import { Select, DatepickerRange } from '@/components/global';
import { AnalyticsFilters } from '@/modules/analytics/types';
import { AnalyticsSections } from '@/enums';

const { state } = widgets_store;

const analytics_state = analytics_store.state;

export interface Lead {
  title: string
  value: number
  total_price?: number | null
}

export interface Leads {
  total_count: number
  items: Lead[]
}

@Component({
  components: {
    apexchart: () => import('vue-apexcharts'),
    Select,
    DatepickerRange,
  },
})
export default class WidgetLeads extends Vue {
  @Prop({ type: Boolean, default: false })
  is_filters!: boolean;

  @Emit()
  filter_update(key: keyof AnalyticsFilters, value: unknown, section: string) {
    analytics_store.mutations.UPD_FILTER_KEY({ key, value, section });
  }

  colors: string[] = [
    '#010A61', '#083DC7', '#477DE4', '#9BB2EC', '#C1CDF0', '#e1e4ed',
  ]

  get lead_regions() {
    return analytics_state.lead_regions;
  }

  get analytics_leads() {
    const response_filters = this.has_response_filters();

    return response_filters
      ? analytics_state.response_filters.leads.items?.filter((item) => item.value)
      : analytics_state.analytics.leads.items?.filter((item) => item.value);
  }

  get analytics_total_count() {
    const response_filters = this.has_response_filters();

    return response_filters
      ? analytics_state.response_filters.leads.total_count
      : analytics_state.analytics.leads.total_count;
  }

  get total_count() {
    return state.widgets.leads?.total_count;
  }

  get lead_items() {
    return state.widgets.leads?.items.filter((item) => item.value);
  }

  has_response_filters() {
    return (
      analytics_state.filters.lead_dates.length > 0
      || analytics_state.filters.region !== null
    );
  }

  xaxis_categories() {
    let result: string[] = [];

    if (this.is_filters) {
      result = this.analytics_leads ? this.analytics_leads?.map((product) => product.title) : [];
    } else {
      result = this.lead_items ? this.lead_items.map((product) => product.title) : [];
    }
    return result;
  }

  get chartOptions() {
    const xaxis_categories = this.xaxis_categories();

    return {
      chart: {
        type: 'bar',
        height: 300,
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 6,
          horizontal: true,
          barHeight: '99.9%',
          isFunnel: true,
          distributed: true,
        },
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '12px',
          fontWeight: '700',
        },
        dropShadow: {
          enabled: false,
        },
      },
      colors: this.colors,
      legend: {
        show: false,
      },
      xaxis: {
        categories: xaxis_categories,
      },
      tooltip: {
        enabled: true,
        y: {
          formatter(y: number) {
            if (typeof y !== 'undefined') {
              return y.toFixed(0);
            }
            return y;
          },
        },
      },
    };
  }

  get series() {
    if (this.is_filters) {
      return [
        {
          name: 'Количество лидов',
          data: this.analytics_leads?.map((item) => item.value),
        },
      ];
    }
    return [
      {
        name: 'Количество лидов',
        data: this.lead_items?.map((item) => item.value),
      },
    ];
  }

  get filters() {
    return analytics_state.filters;
  }

  get leads_loading() {
    return analytics_state.leads_loading;
  }

  handle_change_dates(dates: string[]) {
    const dates_unformatted = [...dates];
    const dates_formatted = dates_unformatted.map((date) => new Date(date));

    if (dates_formatted[1] < dates_formatted[0]) {
      [
        dates_unformatted[0], dates_unformatted[1],
      ] = [
        dates[1], dates[0],
      ];
    }
    this.filter_update('lead_dates', dates_unformatted, 'leads');
  }
}
</script>

<style module lang="scss">
@import '@/scss/common/filter-blocks.scss';

.chart_wrapper {
  width: 100%;
}
.leads_widget {
  display: flex;
  flex-direction: column;
  padding: 24px;
}
.leads_widget_title {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.leads_discription_items {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
}
.leads_widget_chart {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 24px;
  width: 100%;
}
.widget_chart_item {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 280px;
  height: 32px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
}
.leads_widget_discription {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.discription_all_leads {
  display: flex;
  justify-content: space-between;
}
.leads_discription_item {
  display: flex;

}
.leads_discription_items {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
}
.leads_discription_label_color {
  width: 20px;
  height: 20px;
  border-radius: 4px;
}
.filterField {
  max-width: 100% !important;
}
.datepickerMargin {
  margin-bottom: -14px;
}

@media (max-width: 599px) {
  .leads_widget_chart {
    padding: 16px 0px;
}
}
</style>
